import { useEffect, useState, useCallback } from "react";
import { safeHtml } from "@/lib/html";
import { useNavigate } from "react-router-dom";
import client, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Spinner } from "@/components/bits";
import { Plug, ExternalLink, Search, ShieldCheck, Lock, ChevronLeft, ChevronRight, FileText, Sparkles } from "lucide-react";

function ConnectForm() {
  const { refreshWp } = useAuth();
  const [url, setUrl] = useState("https://www.entrancezone.com/admissions");
  const [username, setUsername] = useState("");
  const [appPassword, setAppPassword] = useState("");
  const [connecting, setConnecting] = useState(false);

  const connect = async (e) => {
    e.preventDefault();
    setConnecting(true);
    try {
      const { data } = await client.post("/wp/connect", { url, username, app_password: appPassword });
      setAppPassword("");
      await refreshWp();
      toast.success(data.read_only ? "Connected (READ-ONLY)" : "WordPress connected");
    } catch (ex) { toast.error(apiErr(ex, "Connection failed")); } finally { setConnecting(false); }
  };

  return (
    <div className="max-w-2xl">
      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <div className="flex items-center gap-2 mb-4"><Plug className="h-5 w-5 text-slate-700" /><h2 className="font-serif text-lg font-bold text-slate-900">Connect via Application Password</h2></div>
        <div className="mb-4 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-600 flex items-start gap-2">
          <Lock className="h-3.5 w-3.5 mt-0.5 text-slate-500" />
          The Application Password is stored only encrypted-at-rest on the server — never sent to the browser, never logged.
        </div>
        <form onSubmit={connect} className="space-y-3">
          <input data-testid="wp-url-input" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://entrancezone.com" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500" />
          <input data-testid="wp-username-input" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="username" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500" />
          <input data-testid="wp-app-password-input" type="password" value={appPassword} onChange={(e) => setAppPassword(e.target.value)} placeholder="Application Password" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500" />
          <button data-testid="wp-connect-btn" disabled={connecting} className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60">{connecting ? "Testing connection…" : "Test & Connect"}</button>
        </form>
      </div>
    </div>
  );
}

export default function WordPress() {
  const { wp, refreshWp } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ posts: [], total: 0, total_pages: 1 });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (p = page, query = q) => {
    setLoading(true);
    try {
      const { data } = await client.get("/wp/posts", { params: { q: query, page: p, per_page: 10 } });
      setData(data);
    } catch (e) { toast.error(apiErr(e, "Could not load posts")); } finally { setLoading(false); }
  }, [page, q]);

  const connected = wp?.connected;
  useEffect(() => { if (connected) load(1, ""); }, [connected]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!wp?.connected) {
    return (
      <div className="max-w-4xl">
        <p className="text-xs font-mono uppercase tracking-widest text-blue-700">Connection Required</p>
        <h1 className="font-serif text-3xl font-bold text-slate-900 mt-1 mb-6">WordPress Posts</h1>
        <ConnectForm />
      </div>
    );
  }

  const doSearch = () => { setPage(1); load(1, q); };

  return (
    <div className="max-w-6xl">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-blue-700">Post Library</p>
          <h1 className="font-serif text-3xl font-bold text-slate-900 mt-1">WordPress Posts</h1>
        </div>
        <div className="flex items-center gap-2">
          {wp?.read_only && <span className="rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-800">READ-ONLY</span>}
          <button onClick={async () => { await client.post("/wp/disconnect"); await refreshWp(); }} className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:border-rose-400 hover:text-rose-600">Disconnect</button>
        </div>
      </div>

      <div className="flex gap-3 mb-5">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input data-testid="post-library-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && doSearch()} placeholder="Search by title, keyword, post ID, or URL…" className="w-full rounded-lg border border-slate-300 pl-10 pr-3 py-2.5 text-sm outline-none focus:border-blue-500" />
        </div>
        <button data-testid="post-library-search-btn" onClick={doSearch} className="rounded-lg bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-slate-800">Search</button>
      </div>

      {loading ? <Spinner label="Loading posts…" /> : (
        <>
          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs font-mono uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3">Post</th>
                  <th className="px-4 py-3">ID</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Categories</th>
                  <th className="px-4 py-3">Modified</th>
                  <th className="px-4 py-3">Last AI</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.posts.map((p) => (
                  <tr data-testid="post-row" key={p.id} onClick={() => navigate(`/wordpress/post/${p.id}`)} className="cursor-pointer hover:bg-blue-50/50">
                    <td className="px-4 py-3 max-w-md">
                      <div className="font-semibold text-slate-800 line-clamp-1" dangerouslySetInnerHTML={{ __html: safeHtml(p.title) }} />
                      <a href={p.url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1">{(p.url || "").slice(0, 48)} <ExternalLink className="h-3 w-3" /></a>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-500">#{p.id}</td>
                    <td className="px-4 py-3"><span className="rounded bg-slate-100 px-2 py-0.5 text-[11px] font-mono text-slate-600">{p.status}</span></td>
                    <td className="px-4 py-3 text-xs text-slate-600">{(p.categories || []).slice(0, 2).join(", ") || "—"}</td>
                    <td className="px-4 py-3 text-xs text-slate-500">{(p.modified || "").slice(0, 10)}</td>
                    <td className="px-4 py-3 text-xs">{p.last_analysis ? <span className="text-emerald-600">{p.last_analysis.slice(0, 10)}</span> : <span className="text-slate-300">never</span>}</td>
                    <td className="px-4 py-3"><span className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700"><Sparkles className="h-3.5 w-3.5 text-blue-500" /> Open</span></td>
                  </tr>
                ))}
                {data.posts.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">No posts found.</td></tr>}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between mt-4">
            <span className="text-xs text-slate-500 font-mono">{data.total} posts · page {page} of {data.total_pages}</span>
            <div className="flex items-center gap-2">
              <button data-testid="prev-page-btn" disabled={page <= 1} onClick={() => { const np = page - 1; setPage(np); load(np, q); }} className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 disabled:opacity-40"><ChevronLeft className="h-3.5 w-3.5" /> Prev</button>
              <button data-testid="next-page-btn" disabled={page >= data.total_pages} onClick={() => { const np = page + 1; setPage(np); load(np, q); }} className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 disabled:opacity-40">Next <ChevronRight className="h-3.5 w-3.5" /></button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
